import React from 'react';
import { motion } from 'framer-motion';

interface Highlight {
  title: string;
  imageUrl?: string;
  // For backward compatibility with old string format
  _id?: string;
}

interface PackageHighlightsProps {
  highlights: (Highlight | string)[];
  packageName: string;
}

const PackageHighlights: React.FC<PackageHighlightsProps> = ({ highlights, packageName }) => {
  console.log('🎯 PackageHighlights received data:', {
    packageName,
    highlights,
    highlightsCount: highlights?.length || 0
  });
  
  // Helper function to convert to title case (e.g., "siem reap, cambodia" -> "Siem Reap, Cambodia")
  const toTitleCase = (str: string) => {
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  };
  
  if (!highlights || highlights.length === 0) {
    console.log('⚠️ No highlights to display');
    return null;
  }

  // Helper function to get Cloudinary optimized URL
  const getOptimizedImageUrl = (imageUrl: string, transformation = 'w_600,h_400,c_fill,q_auto,f_auto') => {
    console.log('🖼️ Processing image URL:', imageUrl);
    
    if (!imageUrl) {
      console.log('⚠️ No image URL provided');
      return '/api/placeholder/400/300';
    }
    
    if (!imageUrl.includes('cloudinary.com')) {
      console.log('📸 Non-Cloudinary image:', imageUrl);
      return imageUrl;
    }
    
    // Insert transformation before the version or image name
    const urlParts = imageUrl.split('/');
    const uploadIndex = urlParts.findIndex(part => part === 'upload');
    if (uploadIndex !== -1) {
      urlParts.splice(uploadIndex + 1, 0, transformation);
      const optimizedUrl = urlParts.join('/');
      console.log('✨ Optimized Cloudinary URL:', optimizedUrl);
      return optimizedUrl;
    }
    
    console.log('🔄 Returning original URL:', imageUrl);
    return imageUrl;
  };

  // Normalize highlights to consistent format
  const normalizedHighlights: Highlight[] = highlights.map((highlight, index) => {
    if (typeof highlight === 'string') {
      return { title: highlight };
    }
    return highlight;
  });

  return (
    <section className="py-16 bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl mb-6 shadow-xl">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-4">
            Package Highlights
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover what makes this {toTitleCase(packageName)} experience truly special
          </p>
          <div className="mt-6 w-24 h-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full mx-auto"></div>
        </motion.div>

        {/* Highlights Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {normalizedHighlights.map((highlight, index) => (
            <motion.div
              key={index}
              className="group relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Image Section */}
              {highlight.imageUrl ? (
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={getOptimizedImageUrl(highlight.imageUrl)}
                    alt={highlight.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      (e.target as HTMLImageElement).src = '/api/placeholder/400/300';
                    }}
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Highlight Badge */}
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                    ✨ Featured
                  </div>
                </div>
              ) : (
                /* Placeholder for highlights without images */
                <div className="h-64 bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-3xl">🏆</span>
                    </div>
                    <div className="text-yellow-800 font-medium">Premium Feature</div>
                  </div>
                </div>
              )}

              {/* Content Section */}
              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
                  {highlight.title}
                </h3>
                
                {/* Decorative Elements */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  {/* <div className="text-sm text-gray-500 font-medium">Included</div> */}
                </div>

                {/* Hover Effect Border */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-yellow-500 to-orange-500 group-hover:w-full transition-all duration-500"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
            </svg>
            Experience All These Amazing Features
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PackageHighlights;
